import { Box, Grid } from "@mui/material";
import OrderCard from "../components/OrderCard.js";
import useResponsiveHelper from "../hooks/useResponsiveHelper.js";
import { useEffect, useMemo, useState } from "react";
import { orderByDistance } from "geolib";
import MetaTags from "../components/MetaTags.js";
import { useLocations } from "../hooks/LocationContext";

export default function Order() {
  const { LOCATIONS } = useLocations();
  const { isMobile, isDesktop, isTablet } = useResponsiveHelper();

  const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
  const [store, setStore] = useState(null);

  // get user location ONCE
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => console.log("Unable to retrieve your location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const hasValidCoords = (p) =>
    p?.latitude != null &&
    p?.longitude != null &&
    Number.isFinite(Number(p.latitude)) &&
    Number.isFinite(Number(p.longitude));

  // build sorted order safely
  const order = useMemo(() => {
    const locations = Array.isArray(LOCATIONS) ? LOCATIONS : [];

    // split locations into valid + invalid coord groups
    const withCoords = locations.filter(hasValidCoords).map((l) => ({
      ...l,
      latitude: Number(l.latitude),
      longitude: Number(l.longitude),
    }));

    const withoutCoords = locations.filter((l) => !hasValidCoords(l));

    // if user coords not ready, don't sort, just show original
    if (!hasValidCoords(userLocation)) return locations;

    const user = {
      latitude: Number(userLocation.latitude),
      longitude: Number(userLocation.longitude),
    };

    const sorted = orderByDistance(user, withCoords);

    // show non-geo locations at the bottom
    return [...sorted, ...withoutCoords];
  }, [LOCATIONS, userLocation]);

  const selected = (
    <OrderCard key={store?.id ?? store?.name} location={store} user={userLocation} selected />
  );

  const desktop = (
    <Box
      sx={{
        maxWidth: "1750px",
        width: isTablet ? "80vw" : "100vw",
        display: "flex",
        justifyContent: "center",
        pb: "30px",
      }}
    >
      <Grid container columnSpacing={1} spacing={2} justifyContent="center" direction="row" alignItems="center">
        {order.map((card) => (
          <Grid
            key={card?.id ?? card?.name}
            item
            sx={{ margin: isMobile ? "7px" : "10px", mb: isMobile ? "-30px" : null }}
            xs={isDesktop ? 5 : 12}
          >
            <OrderCard
              location={card}
              user={userLocation}
              onClick={() => {
                setStore(card);
                document.documentElement.scrollTop = 0;
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <MetaTags title="CoCo Edmonton Ordering" description="List of stores to order from" url={window.location.href} />
      {store ? selected : desktop}
    </Box>
  );
}